import { useEffect, useRef, useState } from 'react';
import { Check, Upload, X } from 'lucide-react';

import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import type { ImageAssetType } from '@/components/ui/carousel/carousel.types';
import { useUploadQueue, type DiscardReport } from './useUploadQueue';
import { MAX_UPLOAD_MB } from './uploadValidation';
import type { UploadContext } from './uploadAssetService';

interface UploadModalProps {
  ctx: UploadContext;
  /** What the upload is for, in the modal's title. Defaults to "Upload Photos". */
  title?: string;
  /** Files handed in by the page-level dropzone, queued on mount. */
  initialFiles?: File[];
  onUploaded: (images: ImageAssetType[]) => void;
  onClose: () => void;
  /**
   * Called when cancelled or removed photos could not be discarded (refused by
   * asset-service, or the request kept failing). They are still in the photoset;
   * the report carries grid-ready versions so the page can show them for deletion.
   * May be called after the modal has closed.
   */
  onDiscardIncomplete?: (report: DiscardReport) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

const UploadModal = ({
  ctx,
  title = 'Upload Photos',
  initialFiles,
  onUploaded,
  onClose,
  onDiscardIncomplete
}: UploadModalProps) => {
  const { files, addFiles, remove, uploading, takeReadyImages, discardAll } = useUploadQueue(ctx, {
    onDiscardIncomplete
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Queue the page-dropped files once and only once. Upload is eager, so a
  // second addFiles would POST each file again and create duplicate assets
  // server-side — and StrictMode double-invokes effects.
  const appliedInitialFiles = useRef(false);
  useEffect(() => {
    if (!initialFiles?.length || appliedInitialFiles.current) return;
    appliedInitialFiles.current = true;
    addFiles(initialFiles);
  }, [initialFiles, addFiles]);

  const ready = files.filter((f) => f.status === 'done');
  const canConfirm = ready.length > 0 && !uploading;

  const confirm = () => {
    if (!canConfirm) return;
    onUploaded(takeReadyImages());
    onClose();
  };

  // Abandoning the modal must leave nothing behind. Upload is eager, so any file that
  // already finished exists server-side and has to be deleted, not just forgotten.
  // The modal closes straight away since nothing on screen depends on the cleanup
  // landing; photos it left behind are reported through onDiscardIncomplete.
  const cancel = () => {
    void discardAll();
    onClose();
  };

  return (
    <Modal open onOpenChange={(o) => !o && cancel()} title={title} size="lg">
      <div className="flex flex-col gap-4">
        <button
          type="button"
          className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm font-medium">
            Drag Photos Here, Or <span className="underline">Browse</span>
          </span>
          <span className="text-xs text-muted-foreground">JPG or PNG · up to {MAX_UPLOAD_MB} MB each</span>
          {/* DV-3735: a caption set by the upload (the Floor Plan tab's) is
              invisible until the photos land, so say it before they are sent. */}
          {ctx.caption && (
            <span className="text-xs text-muted-foreground">
              Every image is captioned “{ctx.caption}”.
            </span>
          )}
          <input
            ref={inputRef}
            data-testid="upload-input"
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
          />
        </button>

        {files.length > 0 && (
          <ul className="flex flex-col gap-2 max-h-64 overflow-y-auto">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-3 rounded-md border border-border p-2">
                {f.previewUrl ? (
                  <img
                    src={f.previewUrl}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded object-cover"
                  />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted text-sm font-medium text-muted-foreground">
                    !
                  </span>
                )}
                <span className="flex-1 truncate text-sm">{f.name}</span>
                {f.status === 'error' ? (
                  <span className="text-xs text-destructive">{f.error}</span>
                ) : f.status === 'uploading' ? (
                  <span className="h-1.5 w-24 rounded-full bg-muted">
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${f.progress}%` }} />
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">{formatFileSize(f.size)}</span>
                )}
                {f.status === 'done' && <Check className="h-4 w-4 text-success" aria-hidden="true" />}
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => remove(f.id)}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={cancel}>Cancel</Button>
          <Button disabled={!canConfirm} onClick={confirm}>
            {uploading ? 'Uploading…' : `Add ${ready.length || ''} ${ready.length === 1 ? 'Photo' : 'Photos'}`.replace('  ', ' ')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadModal;
